import React, { useState, useContext, useEffect } from 'react';
import styled from 'styled-components';
import { FirebaseContext, LocalContext } from 'context';
import { AnimatePresence } from 'motion/react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { Button, Message } from 'components';
import { navigate, useStaticQuery, graphql } from 'gatsby';

function LoginForm({ sessionId, sessionSlug }) {
  const { firebase, user } = useContext(FirebaseContext);
  const { showLoginDialog, setShowLoginDialog, handleDialog, loginDialogRef } =
    useContext(LocalContext);
  const [submittingForm, setSubmittingForm] = useState(false);
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const data = useStaticQuery(graphql`
    query {
      allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/(src/data/sessions)/" } }
      ) {
        nodes {
          frontmatter {
            id
            slug
          }
        }
      }
    }
  `);

  const sessions = data.allMarkdownRemark.nodes.map((node) => node.frontmatter);

  useEffect(() => {
    if (firebase && user && user.sessionsUserHasAccessTo?.length > 0) {
      // Use the first session from user's accessible sessions
      const defaultSessionId = user.sessionsUserHasAccessTo[0];
      const defaultSession = sessions.find(
        (session) => session.id === defaultSessionId
      );

      if (defaultSession) {
        navigate(`/sessions/${defaultSession.slug}`);
      }
    }
  }, [firebase, user, sessions]);

  const handleInputChange = (e) => {
    e.persist();

    if (errorMessage) {
      setErrorMessage('');
    }

    setEmail(e.target.value);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (email && firebase && firebase.auth) {
      setSubmittingForm(true);
      try {
        const { data: pw } =
          await firebase.userProfile.checkIfUserCanLogInDirectly({ email });
        await signInWithEmailAndPassword(
          firebase.auth,
          email.toLowerCase().trim(),
          pw
        );
        if (showLoginDialog) {
          handleDialog({
            dialogRef: loginDialogRef,
            animation: 'close',
            stateHandler: () => setShowLoginDialog(false)
          });
        }
      } catch (error) {
        console.error(error);
        setErrorMessage(error.message);
        setSubmittingForm(false);
      }
    }
  };

  console.log(errorMessage);

  return (
    <Container>
      <h2>Login</h2>
      <form onSubmit={handleFormSubmit}>
        <label label="email">
          <input
            name="email"
            type="email"
            onChange={handleInputChange}
            value={email}
            placeholder="Enter Your Email Address"
            autoFocus
            required
          />
        </label>
        <ConsentWrapper>
          <ConsentCheckbox
            type="checkbox"
            id="consent"
            checked={consent}
            onChange={(e) => setConsent(e.target.checked)}
            required
          />
          <ConsentLabel htmlFor="consent">
            I consent to the processing of my personal data (name and email
            address) for the purpose of granting me access to this platform and
            to allow me to provide feedback on the platform. I understand that
            my personal data will be processed by Agency X on behalf of
            AstraZeneca and Daiichi Sankyo for the management of this platform
            and will be deleted 30 days after the end of the event. You will
            find more information in the Data protection notice below.
          </ConsentLabel>
        </ConsentWrapper>
        <Button
          type="submit"
          loading={submittingForm}
          width="120px"
          changeColorWhenDisabled={false}
          shine>
          Login
        </Button>
      </form>
      <AnimatePresence>
        {errorMessage && (
          <Message
            type="error"
            message={
              errorMessage === 'Firebase: Error (auth/user-disabled).'
                ? 'The session has ended. Thank you for participating.'
                : errorMessage
            }
          />
        )}
      </AnimatePresence>
    </Container>
  );
}

const Container = styled.section`
  background-color: #fff;
  border: 1px solid #e2e4ea;
  border-radius: var(--border-radius-md);
  margin: 0 auto;
  max-width: 20.938rem;
  overflow: hidden;
  padding: 3.25rem 1.25rem 2.5rem;
  perspective: 50rem;
  position: relative;
  width: 100%;
  z-index: 1;

  @media (min-width: 1200px) {
    max-width: 28.375rem;
    padding: 3.25rem 1.875rem 2.5rem;
  }

  h2,
  p {
    text-align: center;
  }

  p {
    margin-bottom: 1.65em;
  }

  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 1em;
    text-transform: uppercase;
  }

  form {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    width: 100%;
  }

  label[label='email'],
  input[type='email'] {
    width: 100%;
  }

  label[label='email'] {
    margin-bottom: 1.188rem;
  }
`;

const ConsentWrapper = styled.div`
  align-items: flex-start;
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  width: 100%;
`;

const ConsentCheckbox = styled.input`
  appearance: none;
  border: 1px solid #e2e4ea;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  height: 24px;
  margin: 0;
  margin-top: 0.125rem;
  max-height: 24px;
  max-width: 24px;
  min-height: 24px;
  min-width: 24px;
  padding: 0;
  position: relative;
  width: 24px;

  &:checked {
    background-color: #0c3998;
    border-color: #0c3998;
  }

  &:checked::after {
    border: solid white;
    border-width: 0 2px 2px 0;
    content: '';
    display: block;
    height: 12px;
    left: 8px;
    position: absolute;
    top: 3px;
    transform: rotate(45deg);
    width: 5px;
  }
`;

const ConsentLabel = styled.label`
  color: grey;
  cursor: pointer;
  flex: 1;
  font-size: 0.75rem;
  line-height: 1.4;
  margin-bottom: 0;
  overflow-wrap: break-word;
  word-wrap: break-word;
`;

export default LoginForm;
